/**
 * Turns the `error` field of a functions-22 error response into a message. Schema
 * rejections come back as an array of { path, message } rather than a string, which would
 * otherwise stringify to "[object Object]" in the toast.
 * @param {string|Array|undefined} error - The error field from the response body.
 * @param {string} fallback - Message to use when the payload carries nothing usable.
 * @returns {string} The message to surface.
 */
export const remoteErrorMessage = (error, fallback) => {
  if (Array.isArray(error)) {
    const joined = error.map((issue) => issue?.message).filter(Boolean).join(', ');
    return joined || fallback;
  }
  return error || fallback;
};

/**
 * Posts to a functions-22 endpoint and returns the parsed body, rejecting on anything that is
 * not a 2xx.
 *
 * Status before parsing, and parsing that tolerates a body that is not JSON: a rejected token
 * comes back as plain text from validate-firebase-id-token, and the functions gateway answers
 * 5xx and timeouts with HTML. Parsing first turns either into a SyntaxError and loses the
 * message. The thrown error carries `status` and the parsed failure `body` for callers that
 * need more than the message.
 *
 * @param {Function} remote - firebase.remote.
 * @param {string} path - The function name.
 * @param {Object} body - The JSON body.
 * @param {string} fallback - Message for a failure whose body carries nothing usable.
 * @returns {Promise<Object>} The parsed response body.
 */
export const postJson = async (remote, path, body, fallback) => {
  const response = await remote(path, body, { method: 'POST' });

  if (!response.ok) {
    const failure = await response.json().catch(() => ({}));
    const error = new Error(remoteErrorMessage(failure?.error, fallback));
    error.status = response.status;
    error.body = failure;
    throw error;
  }

  return response.json();
};

/**
 * Whether a failed request to an endpoint that records its work on a run document left no run
 * to follow.
 *
 * The endpoint's own `runCreated` is worth more than the status code, either way: `false` on a
 * 5xx means the failure came before the document was written, and following it would wait on a
 * document that cannot arrive; `true` on a 4xx means the run was opened (and marked failed)
 * before the request was turned down. Without it, a 4xx means the request was turned away
 * before any run existed — the token, the schema, an unknown id are all checked first — and a
 * 5xx means the endpoint may be part-way through a run it owns, which only the document can
 * settle.
 *
 * @param {number} status - The response status.
 * @param {Object} failure - The parsed failure body.
 * @returns {boolean}
 */
export const isRunRejected = (status, failure) => {
  const runCreated = failure?.runCreated;
  return runCreated === false || (runCreated !== true && status < 500);
};

/**
 * postJson for an endpoint that records its work on a run document whose id the caller
 * reserved. A rejection carries `isRunRejected` (see above) whenever there was a response; a
 * network failure has none, because the run may have started and is to be followed.
 *
 * @param {Function} remote - firebase.remote.
 * @param {string} path - The function name.
 * @param {Object} body - The JSON body, carrying the reserved runId.
 * @param {string} fallback - Message for a failure whose body carries nothing usable.
 * @returns {Promise<Object>} The parsed response body.
 */
export const postRun = async (remote, path, body, fallback) => {
  try {
    return await postJson(remote, path, body, fallback);
  } catch (error) {
    if (error.status) {
      error.isRunRejected = isRunRejected(error.status, error.body);
    }
    throw error;
  }
};
